"use strict";
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
exports.__esModule = true;
exports.ChargeSelector = void 0;
var jsx_runtime_1 = require("react/jsx-runtime");
/**
 * ChargeSelector Component
 *
 * Purpose:
 *  These changes were implemented to support **keyboard sequencing** and improve accessibility
 *  by allowing users to navigate and activate invoice choices using only the keyboard.

 *
 * Enhancements:
 * - Added support for auto-focusing the first active invoice on mount.
 *   - Created a `firstActiveRef` using `React.createRef<HTMLDivElement>()`.
 *   - Passed the ref to the first <InvoiceChoice /> element via `ref` prop.
 *   - Used `componentDidMount()` to focus the first invoice if available.
 *
 * - Updated <InvoiceChoice /> component to support receiving a `ref` from parent.
 *   - Converted <InvoiceChoice /> from React.FC to `React.forwardRef`.
 *   - Attached `ref` to the root <div> for DOM access.
 *
 * - Added keyboard accessibility:
 *   - Pressing `Enter` while focused on an invoice triggers `handleAddCharge()`.
 *
 * Note:
 * - `React.forwardRef` is necessary because functional components do not receive `ref` directly.
 * - We only pass the `ref` to the **first** invoice to avoid conflicts.
 */
var react_1 = __importDefault(require("react"));
var classnames_1 = __importDefault(require("classnames"));
var pluralize_1 = __importDefault(require("pluralize"));
var ui_common_1 = require("@codametrix/ui-common");
// ---------------------- utilities  ---------------------- //
// React.forwardRef is used to allow the parent component to attach a ref
// to this functional component, so that the parent can access the underlying DOM node.
var InvoiceChoice = react_1["default"].forwardRef(function (_a, ref) {
    var invoice = _a.invoice, handleAddCharge = _a.handleAddCharge;
    return ((0, jsx_runtime_1.jsxs)("div", __assign({ ref: ref, onClick: function () { return handleAddCharge(invoice); }, onKeyDown: function (e) {
            if (e.key === 'Enter') {
                handleAddCharge(invoice);
            }
        }, tabIndex: 0, className: (0, classnames_1["default"])('qa-invoice-box invoice-box', {
            'invoice-removed': invoice.deleted
        }) }, { children: [(0, jsx_runtime_1.jsxs)("p", __assign({ className: "invoice-box-title" }, { children: [invoice.billing_provider_name, invoice.billing_location && " at ".concat(invoice.billing_location)] })), (0, jsx_runtime_1.jsxs)("small", __assign({ className: "invoice-box-subtitle" }, { children: [invoice.date_of_service
                        ? ui_common_1.cmxDateTime.format(invoice.date_of_service, ui_common_1.cmxDateTime.FORMATS.LONG_DATE)
                        : 'Date not available', invoice.deleted && (0, jsx_runtime_1.jsx)("span", { children: " - Previously removed charge" })] }))] })));
});
var ChargeSelector = /** @class */ (function (_super) {
    __extends(ChargeSelector, _super);
    function ChargeSelector(props) {
        var _this = _super.call(this, props) || this;
        _this.handleOpenClick.bind(_this);
        // Create a ref to focus the first active invoice when the component mounts.
        // This ref is passed to the first <InvoiceChoice /> component.
        _this.firstActiveRef = react_1["default"].createRef();
        _this.state = { open: false };
        return _this;
    }
    ChargeSelector.prototype.componentDidMount = function () {
        var _a;
        // On mount, if the ref is attached, focus the first active invoice.
        if (this.firstActiveRef.current) {
            (_a = this.firstActiveRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    };
    ChargeSelector.prototype.handleOpenClick = function (event) {
        event.preventDefault();
        var state = __assign({}, this.state);
        state.open = !state.open;
        this.setState(state);
    };
    ChargeSelector.prototype.render = function () {
        var _this = this;
        var _a = this.props, invoiceOptions = _a.invoiceOptions, handleAddCharge = _a.handleAddCharge, handleInvoiceAddition = _a.handleInvoiceAddition, className = _a.className, rest = __rest(_a, ["invoiceOptions", "handleAddCharge", "handleInvoiceAddition", "className"]);
        var invoices = invoiceOptions.invoices;
        var styleClasses = (0, classnames_1["default"])(['charge-selector', className]);
        var activeInvoices = invoices.filter(function (invoice) { return !invoice.deleted; });
        var inactiveInvoices = invoices.filter(function (invoice) { return invoice.deleted; });
        return ((0, jsx_runtime_1.jsxs)("div", __assign({ className: styleClasses }, { children: [activeInvoices.map(function (invoice, idx) { return (
                // Pass the `ref` only to the first <InvoiceChoice /> so it can be focused on mount.
                // Since InvoiceChoice is a functional component, we wrapped it in React.forwardRef.
                (0, jsx_runtime_1.jsx)(InvoiceChoice, { invoice: invoice, handleAddCharge: handleAddCharge, ref: idx === 0 ? _this.firstActiveRef : undefined }, idx)); }), (0, jsx_runtime_1.jsx)("div", __assign({ tabIndex: 0, onClick: function () {
                        return handleInvoiceAddition(invoices);
                    }, onKeyDown: function (e) {
                        if (e.key === 'Enter') {
                            handleInvoiceAddition(invoices);
                        }
                    }, className: "qa-invoice-box qa-new-invoice invoice-box" }, { children: (0, jsx_runtime_1.jsx)("p", __assign({ className: "invoice-box-title" }, { children: "A different provider or date of service." })) }), 'qa-invoice-new-invoice'), inactiveInvoices.length > 0 && ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)("small", { children: (0, jsx_runtime_1.jsxs)("a", __assign({ href: "#show", className: "qa-show-all", onClick: function (e) { return _this.handleOpenClick(e); } }, { children: [this.state.open ? 'Hide' : 'Show', ' ', "".concat(inactiveInvoices.length), " previous removed", ' ', (0, pluralize_1["default"])('charge', inactiveInvoices.length), "."] })) }), this.state.open &&
                            inactiveInvoices.map(function (invoice, idx) { return ((0, jsx_runtime_1.jsx)(InvoiceChoice, { invoice: invoice, handleAddCharge: handleAddCharge }, idx)); })] }))] })));
    };
    ChargeSelector.displayName = 'ChargeSelector';
    return ChargeSelector;
}(react_1["default"].Component));
exports.ChargeSelector = ChargeSelector;
//# sourceMappingURL=charge-selector.js.map